import React, { useState, useEffect } from "react";
import { Film } from "lucide-react";
import { getPopularMovies, getImageUrl } from "../../services/tmdbApi";
import { FilterBar } from "../../components/FilterBar";
import "./style.css";

const ITEMS_PER_PAGE = 8;
const MAX_RESULTS = 100;

const MoviesPage = () => {
  const [movies, setMovies] = useState([]);
  const [filteredMovies, setFilteredMovies] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchAllMovies = async () => {
      let allMovies = [];
      for (let page = 1; page <= 5; page++) {
        const pageMovies = await getPopularMovies(page);
        allMovies = allMovies.concat(pageMovies);
        if (allMovies.length >= MAX_RESULTS) break;
      }
      allMovies = allMovies.slice(0, MAX_RESULTS);
      setMovies(allMovies);
      setFilteredMovies(allMovies);
      setLoading(false);
    };
    fetchAllMovies();
  }, []);

  // Dummy filter handler (replace with real logic if needed)
  // Filter logic
  const handleFilterChange = (filters) => {
    let filtered = movies;

    // Search filter
    if (filters.search) {
      filtered = filtered.filter(
        (show) =>
          show.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          show.overview.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    // Rating filter
    if (filters.rating && filters.rating !== "all") {
      const [min, max] = filters.rating.split("-").map(Number);
      filtered = filtered.filter(
        (show) => show.vote_average >= min && show.vote_average <= max
      );
    }

    // Sorting
    switch (filters.sortBy) {
      case "rating-high":
        filtered = [...filtered].sort(
          (a, b) => b.vote_average - a.vote_average
        );
        break;
      case "rating-low":
        filtered = [...filtered].sort(
          (a, b) => a.vote_average - b.vote_average
        );
        break;
      case "title":
        filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "oldest":
        filtered = [...filtered].sort((a, b) =>
          new Date(a.first_air_date).getTime() - new Date(b.first_air_date).getTime()
        );
        break;
      default: // newest
        filtered = [...filtered].sort((a, b) =>
          new Date(b.first_air_date).getTime() - new Date(a.first_air_date).getTime()
        );
    }

    setFilteredMovies(filtered);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredMovies.length / ITEMS_PER_PAGE);
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIdx = startIdx + ITEMS_PER_PAGE;
  const paginatedMovies = filteredMovies.slice(startIdx, endIdx);

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  if (loading) {
    return (
      <div className="movies-loading-container">
        <div className="movies-loading-spinner" />
        <span>Loading movies...</span>
      </div>
    );
  }

  return (
    <div className="movies-page-container">
      <div className="movies-page-content">
        {/* Header */}
        <div className="movies-header">
          <div className="movies-header-icon">
            <Film size={28} />
          </div>
          <div>
            <h1 className="movies-title">Movie Reviews</h1>
            <p className="movies-subtitle">
              Popular movies from The Movie Database
            </p>
          </div>
        </div>
        <div className="movies-header-row">
          <p className="movies-count">
            Showing {filteredMovies.length} of {movies.length} movies
          </p>
          <div className="movies-view-toggle">
            <button
              className={`movies-toggle-btn${viewMode === "grid" ? " active" : ""}`}
              onClick={() => setViewMode("grid")}
            >
              Grid
            </button>
            <button
              className={`movies-toggle-btn${viewMode === "list" ? " active" : ""}`}
              onClick={() => setViewMode("list")}
            >
              List
            </button>
          </div>
        </div>

        <div className="movies-filter-bar">
          <FilterBar category="movies" onFilterChange={handleFilterChange} />
        </div>

        {/* Movies Grid/List */}
        <div
          className={
            viewMode === "grid"
              ? "movies-grid"
              : "movies-list"
          }
        >
          {paginatedMovies.map((movie) => (
            <div
              key={movie.id}
              className={
                viewMode === "list"
                  ? "movies-list-card"
                  : "movies-grid-card"
              }
            >
              <img
                src={getImageUrl(movie.poster_path)}
                alt={movie.title}
                className="movies-card-poster"
              />
              <div className="movies-card-content">
                <h3 className="movies-card-title">{movie.title}</h3>
                <p className="movies-card-date">
                  {movie.release_date
                    ? new Date(movie.release_date).getFullYear()
                    : "TBA"}
                </p>
                <p className="movies-card-excerpt">
                  {movie.overview && movie.overview.length > 120
                    ? movie.overview.substring(0, 120) + "..."
                    : movie.overview}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="movies-pagination">
            <button
              className="movies-pagination-btn"
              onClick={handlePrev}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="movies-pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="movies-pagination-btn"
              onClick={handleNext}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}

        {/* No Results */}
        {paginatedMovies.length === 0 && !loading && (
          <div className="movies-no-results">
            <Film size={48} />
            <h3>No movies found</h3>
            <p>Try adjusting your filters to find what you're looking for.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoviesPage;